import React from 'react';
import { HOME_LOANS_VISIBLE } from '../../lib/homeLoans';
import { ConsumerCreditDisclosure } from '../home-loans/ConsumerCreditDisclosure';
import { Link, useLocation } from 'react-router-dom';
import { Linkedin, Mail } from 'lucide-react';
import { ENTITY } from '@/lib/entity';
import { PublicStatusPill } from './PublicStatusPill';

/**
 * InstitutionalFooter — Phase 99A light direction.
 *
 * Coheres with the warm-paper public site. Uses the canonical brand-coloured
 * logo (gp-capital-stacked-frame.svg = navy frame + teal P) so the logo
 * cannot be themed or recoloured by palette passes. Text colours, hairlines
 * and CTAs all reference `--gpc-*` tokens so future colour tuning happens in
 * one place.
 */
export const InstitutionalFooter = ({ consumerCredit = false, config }) => {
  const { pathname: rawPathname } = useLocation();
  // Nginx may redirect generated page directories to a trailing-slash URL.
  const pathname = rawPathname.replace(/\/+$/, "") || "/";
  const privateCapital = !consumerCredit && (["/capital-solutions", "/for-borrowers", "/for-lenders", "/disclosures", "/platform-terms"].includes(pathname) || pathname.startsWith("/insights/"));
  const currentYear = new Date().getFullYear();

  return (
    <footer
      style={{ background: 'var(--gpc-panel)', color: 'var(--gpc-ink)', borderTop: '1px solid var(--gpc-line)' }}
      data-testid="institutional-footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8"><Link to="/tools" className="gpc-cta-secondary">Finance calculators &amp; tools →</Link></div>
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              {/* Brand asset — canonical original colours, locked. */}
              <img
                src="/gp-capital-stacked-frame.svg"
                alt="GP Capital"
                width="40"
                height="40"
                data-brand-asset="gp-cap-logo"
              />
              <div>
                <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontWeight: 600, fontSize: '1.125rem', color: 'var(--gpc-ink)' }}>
                  GP Capital
                </span>
                <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.75rem', color: 'var(--gpc-ink-soft)' }}>
                  Home Loans &amp; Private Capital
                </p>
              </div>
            </div>
            <p
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.875rem',
                color: 'var(--gpc-ink-soft)',
                lineHeight: 1.6,
                maxWidth: '24rem',
                marginBottom: '1.5rem',
              }}
            >
              An Australian finance business with Private Capital solutions and a Home Loans pathway in preparation. Separate services, one disciplined approach.
            </p>
            <div className="flex gap-3">
              <a
                href={ENTITY.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="footer-social"
                data-testid="footer-linkedin"
                aria-label="GP Capital on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={`mailto:${ENTITY.contact.general}?subject=${encodeURIComponent(ENTITY.mailSubjects.general)}`}
                className="footer-social"
                data-testid="footer-email"
                aria-label="Email GP Capital"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Solutions */}
          <FooterColumn title="Home Loans">
            {HOME_LOANS_VISIBLE && <FooterLink to="/home-loans">Home Loans</FooterLink>}
            <FooterLink to="/tools/mortgage-repayment-calculator">Repayment Calculator</FooterLink>
            <FooterLink to="/tools/stamp-duty-calculator">Stamp Duty — Verification Status</FooterLink>
            <FooterLink to="/tools">Finance Tools</FooterLink>
          </FooterColumn>
          <FooterColumn title="Private Capital">
            <FooterLink to="/capital-solutions">Capital Solutions</FooterLink>
            <FooterLink to="/for-borrowers">For Borrowers</FooterLink>
            <FooterLink to="/for-lenders">For Capital Partners</FooterLink>
            <FooterLink to="/contact">Submit a Deal</FooterLink>
          </FooterColumn>

          {/* Company */}
          <FooterColumn title="Company">
            <FooterLink to="/about">About</FooterLink>
            <FooterLink to="/insights">Insights</FooterLink>
            <FooterLink to="/portal/login">Investor Access</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
            <FooterLink to="/book-appointment">Book an appointment</FooterLink>
          </FooterColumn>

          {/* Legal */}
          <FooterColumn title="Legal">
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms">Terms of Service</FooterLink>
            <FooterLink to="/disclosures">Disclosures</FooterLink>
            <FooterLink to="/platform-terms">Platform Terms</FooterLink>
            <FooterLink to="/security" testId="footer-security">Security</FooterLink>
            <FooterLink to="/trust" testId="footer-trust">Trust Center</FooterLink>
            <FooterLink href="/.well-known/security.txt" testId="footer-security-txt">Security disclosures</FooterLink>
          </FooterColumn>
        </div>
      </div>

      {/* Bottom Bar */}
      <div style={{ borderTop: '1px solid var(--gpc-line-soft)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div data-testid="footer-legal">
              <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.75rem', color: 'var(--gpc-ink-soft)' }}>
                © {currentYear} GP Capital. All rights reserved.
              </p>
              <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.6875rem', color: 'var(--gpc-ink-soft)', marginTop: '4px' }}>
                {ENTITY.legalName} · ACN {ENTITY.acn} · ABN {ENTITY.abn}
              </p>
              <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '0.6875rem', color: 'var(--gpc-ink-soft)', marginTop: '2px' }}>
                {privateCapital && "Private Capital: Wholesale / sophisticated counterparties only."}
              </p>
              {privateCapital && <PublicStatusPill className="mt-2" />}
            </div>
            {consumerCredit ? <ConsumerCreditDisclosure config={config} /> : privateCapital ? <p
              style={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.75rem',
                color: 'var(--gpc-ink-soft)',
                maxWidth: '36rem',
                textAlign: 'right',
              }}
              className="text-center md:text-right"
            >
              This website is for informational purposes only and does not constitute an offer to sell or a solicitation of an offer to buy any security. Past performance is not indicative of future results.
            </p> : <div data-testid="company-disclosure" className="company-note" style={{maxWidth:"36rem"}}><p>Private Capital is for wholesale / sophisticated counterparties only.</p>{HOME_LOANS_VISIBLE && <ConsumerCreditDisclosure config={config} />}</div>}
          </div>
        </div>
      </div>

      <style>{`
        .footer-social {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: var(--gpc-paper-2);
          border: 1px solid var(--gpc-line);
          color: var(--gpc-ink);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }
        .footer-social:hover {
          border-color: var(--gpc-line-strong);
          background: var(--gpc-paper);
        }
        .footer-nav-link {
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 0.875rem;
          color: var(--gpc-ink-soft);
          transition: color 0.2s ease;
        }
        .footer-nav-link:hover {
          color: var(--gpc-ink);
        }
      `}</style>
    </footer>
  );
};

const FooterColumn = ({ title, children }) => (
  <div>
    <h4
      style={{
        fontFamily: 'Space Grotesk, system-ui, sans-serif',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: 'var(--gpc-ink)',
        marginBottom: '1rem',
      }}
    >
      {title}
    </h4>
    <ul className="space-y-3">{children}</ul>
  </div>
);

const FooterLink = ({ to, href, children, testId }) => (
  <li>
    {to ? (
      <Link to={to} className="footer-nav-link" data-testid={testId}>{children}</Link>
    ) : (
      <a href={href} className="footer-nav-link" data-testid={testId}>{children}</a>
    )}
  </li>
);

export default InstitutionalFooter;
